<!--
 * @Description: 表格底部的报表图标按钮
 * @version: 1.0.0
 * @Author: Monkey
 * @Date: 2025-07-04 10:10:04
 * @LastEditors: Monkey
 * @LastEditTime: 2025-07-09 18:22:17
-->>
<template>
  <div class="aj-icon-table-wrapper" ref="tableWrapperRef">
    <table class="aj-icon-table">
      <colgroup>
        <col v-for="(col, index) in columns" :key="`col-${index}`" :width="getColWidth(col)" />
      </colgroup>
      <tbody>
        <tr class="aj-icon-row-tr">
          <td v-for="(col, index) in columns" :key="`icon-${index}`" :class="col.className"
            :align="col.align || 'center'" :style="getCellStyle(index)">
            <template v-if="getIconConfig(index)">
              <el-tooltip :content="getIconConfig(index).tooltip || ''" placement="top"
                :disabled="!getIconConfig(index).tooltip">
                <div class="icon-container" @click="handleClick(getIconConfig(index), col, index)">
                  <el-icon class="icon">
                    <component :is="getIconComponent(getIconConfig(index).icon)" />
                  </el-icon>
                  <span v-if="getIconConfig(index).label" class="icon-label">
                    {{ getIconConfig(index).label }}
                  </span>
                </div>
              </el-tooltip>
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'

const props = defineProps({
  columns: {
    type: Array,
    required: true,
    default: () => []
  },
  /**
   * {
   *   prop: 'columnProp',    // 必需：对应表格列的prop字段
   *   icon: 'IconName',      // 必需：Element Plus图标名称
   *   label: '按钮文字',      // 可选：图标下方显示的文字
   *   tooltip: '提示信息',    // 可选：鼠标悬停提示
   *   bgColor: '#color',     // 可选：背景颜色
   *   chart: true,           // 可选：点击后调用dialog显示图标
   *   onClick: () => {}      // 可选：点击事件处理函数
   * }
   */
  iconConfig: {
    type: Array,
    default: () => []
  },
  // 滚动同步相关
  tableScrollLeft: {
    type: Number,
    default: 0
  }
})

// 滚动同步相关
const tableWrapperRef = ref(null)

const emit = defineEmits(['icon-click'])

// 获取列的图标配置
const getIconConfig = (index) => {
  return props.iconConfig[index] || null
}

// 获取图标组件
const getIconComponent = (iconName) => {
  return ElementPlusIconsVue[iconName] || null
}

// 处理点击事件
const handleClick = (iconConfig, column, index) => {
  emit('icon-click', {
    iconConfig,
    column,
    index
  })
}

// 获取列宽度
const getColWidth = (col) => {
  if (col?.width) {
    return typeof col.width === 'number' ? `${col.width}px` : col.width
  } else if (col?.minWidth) {
    return typeof col.minWidth === 'number' ? `${col.minWidth}px` : col.minWidth
  }
  return 'auto'
}

// 获取单元格样式
const getCellStyle = (index) => {
  const config = getIconConfig(index)

  const baseStyle = {
    backgroundColor: config?.bgColor || '',
    cursor: config?.onClick ? 'pointer' : 'default'
  }

  return baseStyle
}

// 同步滚动位置
const syncScroll = () => {
  if (tableWrapperRef.value && props.tableScrollLeft !== undefined) {
    tableWrapperRef.value.scrollLeft = props.tableScrollLeft
  }
}

// 监听滚动位置变化
watch(() => props.tableScrollLeft, () => {
  syncScroll()
})

onMounted(() => {
  // 组件挂载后立即同步一次滚动位置
  if (props.tableScrollLeft !== undefined) {
    syncScroll()
  }
})

// 暴露方法给父组件
defineExpose({
  syncScroll
})
</script>

<style lang="scss" scoped>
.aj-icon-table-wrapper {
  width: 100%;
  overflow-x: hidden;
  /* 隐藏滚动条 */
  scrollbar-width: none;
  /* Firefox */
  -ms-overflow-style: none;
  /* IE and Edge */

  &::-webkit-scrollbar {
    display: none;
    /* Chrome, Safari, Opera */
  }
}

.aj-icon-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  margin: 0 -2px;
  padding: 0;
}

.aj-icon-row-tr {
  height: 46px;
  background-color: #f8f9fa;

  td {
    padding: 0;
    text-align: center;
    border-top: 1px solid #ebeef5;
    border-left: 1px solid #ebeef5;
    vertical-align: middle;
    transition: all 0.3s ease;
    box-sizing: border-box;
    overflow: hidden;

    &:last-child {
      border-right: 1px solid #ebeef5;
    }

    &:hover {
      background-color: #f0f7ff;
    }

    .icon-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      padding: 4px 0;
      cursor: pointer;

      .icon {
        font-size: 18px;
        transition: all 0.3s;
        border-radius: 4px;
        display: inline-block;
        color: #606266;
        margin-bottom: 4px;
      }

      .icon-label {
        font-size: 12px;
        color: #606266;
        transition: color 0.3s;
      }

      &:hover {
        .icon {
          color: var(--el-color-primary);
          transform: scale(1.15);
          background-color: rgba(64, 158, 255, 0.1);
        }

        .icon-label {
          color: var(--el-color-primary);
        }
      }
    }
  }
}
</style>